import { Box, IconButton, Text } from '@youversion/design-system-components';
import CheckmarkStroked from '@youversion/design-system-icons/lib/CheckmarkStroked';
import XStroked from '@youversion/design-system-icons/lib/XStroked';
import Modal from 'components/ui/modal';
import ModalHeader from 'components/ui/modal/Header';
import { useQueryParams } from 'hooks/useQueryParams';
import { useSelectorEvents } from 'hooks/useSelectorEvents';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { firebaseLogEvent } from 'utils/firebase';
import { useCurrency } from 'utils/store/currency';
import { usePaymentMethod } from 'utils/store/paymentMethod';

import GiveOptionListItem from '../controls/GiveOptionListItem';
import RadioListItem from '../controls/RadioListItem';
import ModalFooter from '../modal/Footer';
interface Props {
  onClose: () => void;
}

interface Currency {
  code: string;
  name: string;
  symbol: string;
}

type GroupedCurrencies = Record<string, Array<Currency>>;

export default function CurrencySelector({ onClose }: Props) {
  const { t } = useTranslation('common');
  const { paymentMethodType } = usePaymentMethod();
  const { currencies, setCurrency, selectedCurrency } = useCurrency();
  const { hide_nav: hideNav } = useQueryParams();
  const { logSelectorDismissedEvent, logSelectorPresentedEvent } = useSelectorEvents('giving_currency');

  const groupedCurrencies = currencies?.reduce((acc: GroupedCurrencies, currency: Currency) => {
    const initial = currency.name[0].toLocaleLowerCase();
    if (!acc[initial]) {
      acc[initial] = [];
    }
    acc[initial].push(currency);
    return acc;
  }, {});

  useEffect(() => {
    firebaseLogEvent('GivingScreenView', { screen: 'giving_currency' });
    logSelectorPresentedEvent(selectedCurrency);
    return () => void logSelectorDismissedEvent(selectedCurrency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal onClose={onClose}>
      <Box className='flex flex-col h-full'>
        <ModalHeader
          left={
            <IconButton className='inline-end-2 block-start-2' data-testid='close-modal-button' onClick={onClose}>
              <XStroked className='text-text-light dark:text-text-dark' />
              <label className='sr-only'>{t('giving.cta.close')}</label>
            </IconButton>
          }
          title={t('giving.titles.currency')}
        />
        {hideNav ? (
          <>
            <Box className='grow overflow-x-auto pli-3'>
              {groupedCurrencies
                ? Object.keys(groupedCurrencies)
                    .sort()
                    .map(currency => (
                      <Box key={currency}>
                        <Text className='mbs-0.5 text-text-light dark:text-text-dark font-bold'>
                          {currency.toUpperCase()}
                        </Text>
                        {groupedCurrencies[currency].map((value: Currency) => {
                          const isSelected = value.code === selectedCurrency;
                          return (
                            <GiveOptionListItem
                              className={isSelected ? 'bg-blue-10 dark:bg-gray-30' : ''}
                              disabled={value.code !== 'USD' && paymentMethodType?.toLowerCase() === 'ach'}
                              key={value.code}
                              label={value.name}
                              onClick={() => {
                                firebaseLogEvent('GivingButtonTap', {
                                  label: 'Currency',
                                  screen: 'giving_currency',
                                  value: value.code,
                                });
                                setCurrency(value.code);
                              }}
                              rightElement={
                                isSelected ? (
                                  <CheckmarkStroked className='text-text-light dark:text-text-dark' />
                                ) : undefined
                              }
                              rightText={value.symbol}
                              title={value.code}
                              value={value.code}
                            />
                          );
                        })}
                      </Box>
                    ))
                : null}
            </Box>
            <ModalFooter
              buttonText={t('giving.cta.done')}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_currency' });
                onClose();
              }}
            />
          </>
        ) : (
          <>
            <Box className='grow overflow-x-auto pli-2'>
              {groupedCurrencies
                ? Object.keys(groupedCurrencies)
                    .sort()
                    .map(initial => (
                      <Box key={initial}>
                        <Text className='mbs-0.5 text-text-light dark:text-text-dark'>{initial.toUpperCase()}</Text>
                        {groupedCurrencies[initial].map((value: Currency) => {
                          const isSelected = value.code === selectedCurrency;
                          return (
                            <RadioListItem
                              className='mlb-2'
                              disabled={value.code !== 'USD' && paymentMethodType?.toLowerCase() === 'ach'}
                              isChecked={isSelected}
                              key={value.code}
                              label={value.name}
                              onChange={() => {
                                firebaseLogEvent('GivingButtonTap', {
                                  label: 'Currency',
                                  screen: 'giving_currency',
                                  value: value.code,
                                });
                                setCurrency(value.code);
                              }}
                              secondaryLabel={value.code}
                              secondaryTitle={value.symbol}
                              value={value.code}
                            />
                          );
                        })}
                      </Box>
                    ))
                : null}
            </Box>
            <ModalFooter
              buttonText={t('giving.cta.done')}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_currency' });
                onClose();
              }}
            />
          </>
        )}
      </Box>
    </Modal>
  );
}
